/**
 * ชนิดข้อมูลของใบสั่งขาย (Sales Order) — เขียนตาม payload contract ที่ freeze แล้ว
 * (`docs/API_CONTRACT.md` — "Sales Order (`SALES`) — Phase 2", D-090)
 *
 * ใบสั่งขายเป็น **ภาพสะท้อนของใบสั่งซื้อ** (P2-2b, D-085): เอกสารหัวใบเดียวที่มี
 * ได้หลายบรรทัด ผูกกับโรงงานเดียว (D-004) ขับด้วย state machine ที่เปลี่ยนสถานะได้
 * ทางเดียวคือ transition ของมันเอง (**ไม่มี `PATCH` และไม่มี `DELETE` ในกลุ่มนี้
 * เลย**) และมีกฎแบ่งแยกหน้าที่ข้อเดียวกัน (D-010) เพียงแต่คู่ค้าอยู่คนละฝั่ง:
 * **Customer PO หนึ่งใบ = Sales Order Header หนึ่งใบ**
 *
 * สองข้อที่ต่างจากใบสั่งซื้อและกำหนดทั้งหน้าจอ:
 *
 * 1. **`GET` รายการตัด `lines` ออก ส่วน `GET /{so_id}` คืนครบ** ต่างจากใบสั่งซื้อที่
 *    ส่งบรรทัดมากับทุกแถวของรายการ — นี่คือ contract ไม่ใช่การปรับแต่งที่ยกเลิกได้
 *    จึงถูกบังคับด้วย **ชนิดข้อมูลสองตัว**: `SalesOrderSummary` (ไม่มีช่องให้ใส่
 *    บรรทัดเลย) และ `SalesOrder` (มีบรรทัดครบ) หน้าจอที่อยากได้บรรทัดจึงต้องไปเอา
 *    จากใบเดียว ไม่ใช่จากแถวในรายการ และ **ไม่มี loop ใดที่ยิงใบเดียวทีละแถว**
 * 2. **บรรทัดถูกกรอกโดยผู้ใช้ทั้งบรรทัด** (สินค้า จำนวน หน่วย และวันที่ต้องการของ)
 *    ต่างจากบรรทัดของใบสั่งซื้อที่คัดลอกมาจากใบขอซื้อโดยเซิร์ฟเวอร์ — Customer PO
 *    เป็นเอกสารของลูกค้าที่เราคีย์เข้าระบบ ไม่ได้เกิดจากเอกสารภายในใบก่อนหน้า
 *
 * ⚠️ **contract ไม่ได้เขียนตัวอย่าง response ของกลุ่มนี้ไว้ทีละฟิลด์** (ต่างจาก
 * Incoming Inspection Rule ที่มีตัวอย่างครบทั้งก้อน) มันเขียนแต่ `POST` body, กฎ
 * ธุรกิจ และประโยค "คืน `201` เป็น SO เต็มใบพร้อม `lines`, `status = \"DRAFT\"`"
 * ชนิดข้อมูลข้างล่างจึง **บังคับเฉพาะฟิลด์ที่ contract เอ่ยชื่อไว้ตรง ๆ** และปล่อย
 * ฟิลด์ประกอบ (UUID คู่ของรหัส ผู้กระทำ และเวลา) เป็น nullable ที่หายไปทั้งคีย์ได้
 * — ดูเหตุผลเต็มที่ `parseSalesOrderSummary()` ใน `client.ts`
 */

/** รหัสโมดูลของหน้าจอนี้ใน RBAC matrix — โมดูล **ธุรกิจ** ปกติ ADMIN ถูก deny (D-060) */
export const SALES_MODULE = 'SALES';

/* ------------------------------------------------------------- สถานะ ----- */

export const DRAFT_SO_STATUS = 'DRAFT';
export const CONFIRMED_SO_STATUS = 'CONFIRMED';
export const PLANNED_SO_STATUS = 'PLANNED';
export const PARTIALLY_DELIVERED_SO_STATUS = 'PARTIALLY_DELIVERED';
export const DELIVERED_SO_STATUS = 'DELIVERED';
export const CANCELLED_SO_STATUS = 'CANCELLED';

/**
 * หกค่าที่ CHECK constraint ฝั่งเซิร์ฟเวอร์รับ ตามลำดับของ lifecycle
 *
 * **สามค่ากลางยังไม่มีใครเขียน** — `PLANNED` เป็นของ MRP (P2-6) ส่วน
 * `PARTIALLY_DELIVERED`/`DELIVERED` เป็นของงานส่งของ (P2-9) ไม่มี route ใดใน P2-4b
 * เขียนสามค่านี้ แต่ทั้งสามอยู่ในชุดนี้เพราะ **หน้าจอต้องอ่านมันออก**: รูปแบบ
 * เดียวกับที่ `PurchaseOrderStatus` ประกาศ `PARTIALLY_RECEIVED`/`CLOSED` ไว้ก่อนที่
 * งานรับเข้าจะมีอยู่ และเมื่อใบไปถึงสามสถานะนั้น **ไม่มีปุ่มใดบนหน้าจอนี้กระทำต่อ
 * ได้อีก** — ของออกจากโรงงานไปแล้วหรือมีแผนผลิตครอบอยู่แล้ว
 */
export const SALES_ORDER_STATUSES = [
  DRAFT_SO_STATUS,
  CONFIRMED_SO_STATUS,
  PLANNED_SO_STATUS,
  PARTIALLY_DELIVERED_SO_STATUS,
  DELIVERED_SO_STATUS,
  CANCELLED_SO_STATUS,
] as const;

export type SalesOrderStatus = (typeof SALES_ORDER_STATUSES)[number];

/** ค่าที่เซิร์ฟเวอร์เติมให้ทุกใบตอนสร้าง — ไม่มีทางส่งค่าอื่นไปแทนได้ */
export const DEFAULT_SO_STATUS = DRAFT_SO_STATUS;

export function isSalesOrderStatus(value: string): value is SalesOrderStatus {
  return (SALES_ORDER_STATUSES as readonly string[]).includes(value);
}

/* -------------------------------------------------------- ข้อจำกัดความยาว -- */

/**
 * `customer_po_number` ≤50 — **กว้างกว่า `so_number` โดยตั้งใจ**
 *
 * มันเป็นเลขของลูกค้า เราคุมรูปแบบไม่ได้ (D-090) การบีบให้เท่ากับเลขของเราเองคือ
 * การตัดเลขที่ลูกค้าส่งมาจริงทิ้งไปเงียบ ๆ แล้วเอกสารสองฝั่งจะอ้างคนละเลข
 */
export const CUSTOMER_PO_NUMBER_MAX_LENGTH = 50;

/** `customer_po_line_no` ≤20 — ห้ามซ้ำภายในใบเดียวกัน (D-090) */
export const CUSTOMER_PO_LINE_NO_MAX_LENGTH = 20;

/**
 * หมายเหตุ — **contract ไม่ได้ระบุความยาวของ `note` ในกลุ่มนี้**
 *
 * 255 คือความกว้างที่คอลัมน์ `note` ของทุกเอกสารในระบบใช้อยู่ (ใบขอซื้อ ใบสั่งซื้อ
 * กฎตรวจรับเข้า) ค่านี้จึงเป็นเพดานของ **ช่องกรอก** ไม่ใช่คำประกาศของ contract
 * ถ้าเซิร์ฟเวอร์รับได้กว้างกว่านี้ ผลคือหน้าจอเข้มกว่าที่จำเป็นหนึ่งช่อง ซึ่งแก้ได้
 * โดยไม่กระทบใบที่บันทึกไปแล้ว — ต่างจากการปล่อยไม่จำกัดแล้วโดน `400` ที่ผู้ใช้
 * แก้เองไม่ถูกเพราะไม่รู้ว่าเกินไปเท่าไร
 */
export const SO_NOTE_MAX_LENGTH = 255;

/** รหัสอ้างอิงที่ฟอร์มกรอก — ความยาวเดียวกับคอลัมน์ของเจ้าของข้อมูลนั้น ๆ */
export const PLANT_CODE_MAX_LENGTH = 20;
export const CUSTOMER_CODE_MAX_LENGTH = 20;
export const PRODUCT_CODE_MAX_LENGTH = 40;
export const UOM_CODE_MAX_LENGTH = 10;

/**
 * `ref1`..`ref6` ≤50 — เท่ากับ `REF_LENGTH` ฝั่งเซิร์ฟเวอร์ซึ่งผูกกับความยาวเดียวกับ
 * `customer_po_number` (`apps/api/.../sales/models.py`, D-107 ข้อ 2) ไม่ใช่ค่าเดา
 */
export const SALES_ORDER_LINE_REF_MAX_LENGTH = 50;

/* ------------------------------------------------------------- กฎธุรกิจ --- */

/**
 * `409` — เลขใบสั่งขายนี้มีเจ้าของอยู่แล้ว **ไม่มีอะไรถูกเขียนเลย**
 *
 * ผู้ใช้แก้เองได้ทันทีด้วยการใช้เลขอื่น จึงต้องไม่ถูกยุบรวมกับ "เซิร์ฟเวอร์ทำงาน
 * ไม่สำเร็จ" ซึ่งเป็นสิ่งที่เขาทำอะไรไม่ได้เลย
 */
export const SO_NUMBER_TAKEN = 'SO_NUMBER_TAKEN';

/**
 * `409` — คู่ (ลูกค้า, เลข PO ของลูกค้า) นี้ถูกรับเข้าเป็นใบสั่งขายไปแล้ว
 *
 * **กฎที่แพงที่สุดของกลุ่มนี้** เพราะสิ่งที่มันกันคือการ *ผลิตและส่งของสองรอบ*
 * สำหรับคำสั่งซื้อใบเดียวของลูกค้า (flow ข้อ 2.3 กันซ้ำที่ระดับตาราง) —
 * ต่างจาก `SO_NUMBER_TAKEN` ที่เป็นเรื่องเลขเอกสารของเราเองชนกัน
 */
export const CUSTOMER_PO_ALREADY_ORDERED = 'CUSTOMER_PO_ALREADY_ORDERED';

/** `403` — โรงงานที่กรอกอยู่นอก `plant_scope` ของผู้เรียก (D-004) */
export const PLANT_OUTSIDE_SCOPE = 'PLANT_OUTSIDE_SCOPE';

/** `422` — ไม่มีลูกค้ารหัสนี้ในทะเบียน */
export const CUSTOMER_NOT_FOUND = 'CUSTOMER_NOT_FOUND';

/**
 * `422` — ลูกค้ารายนี้ถูก **ระงับ** รับคำสั่งซื้อใหม่ไม่ได้
 *
 * เป็นการตัดสินของฝ่ายที่ดูแลทะเบียนลูกค้า (เครดิต/compliance) ไม่ใช่เรื่องสิทธิ์
 * ของผู้คีย์ใบ — ทางแก้คือให้ผู้ดูแลทะเบียนปลดสถานะระงับ ไม่ใช่ขอสิทธิ์เพิ่ม
 */
export const CUSTOMER_IS_BLOCKED = 'CUSTOMER_IS_BLOCKED';

/**
 * `422` — ลูกค้ารายนี้ไม่ได้ `ACTIVE` แล้ว (เลิกค้าขายไปแล้ว)
 *
 * **กฎพี่น้องของข้างบน ไม่ใช่กฎเดียวกัน**: ผู้ที่เห็น `CUSTOMER_IS_BLOCKED` กับ
 * ลูกค้าที่แค่ถูกเลิกใช้ จะออกไปตามหาปัญหาด้านเครดิตที่ไม่มีอยู่จริง
 */
export const CUSTOMER_IS_INACTIVE = 'CUSTOMER_IS_INACTIVE';

/** `422` — เลขบรรทัด PO ของลูกค้าซ้ำกันภายในใบเดียวกัน */
export const CUSTOMER_PO_LINE_NO_DUPLICATED = 'CUSTOMER_PO_LINE_NO_DUPLICATED';

/** `422` — ใบที่ไม่มีบรรทัดเลยไม่ได้สั่งซื้ออะไร */
export const SALES_ORDER_HAS_NO_LINES = 'SALES_ORDER_HAS_NO_LINES';

/** `422` — มีสินค้ารหัสนี้แต่ยังไม่ `ACTIVE` (ยังไม่ผ่านอนุมัติ/ถูกเลิกใช้) */
export const PRODUCT_NOT_ACTIVE = 'PRODUCT_NOT_ACTIVE';

/**
 * `422` — ผู้ยืนยันต้องไม่ใช่ผู้เปิดใบ (D-010, segregation of duties)
 *
 * **ไม่ใช่ `403`**: ผู้กดถือ `SALES` + EDIT ครบจริง สิ่งที่เขาทำไม่ได้คือยืนยัน
 * **ใบนี้ใบเดียว** การแสดงมันเป็น "ไม่มีสิทธิ์" จะส่งเขาไปขอสิทธิ์เพิ่มกับผู้ดูแล
 * ระบบเพื่อแก้สิ่งที่ทำงานถูกต้องอยู่แล้ว — และกฎข้อนี้มี CHECK ในฐานข้อมูลหนุนอยู่
 */
export const CONFIRMER_MUST_DIFFER_FROM_CREATOR = 'CONFIRMER_MUST_DIFFER_FROM_CREATOR';

/**
 * `422` — ใบเดิมเลื่อนพ้น `DRAFT`/`CONFIRMED` ไปแล้วระหว่างที่ยังไม่กดส่ง (D-092)
 *
 * เกิดได้เฉพาะทาง `POST /v1/sales-orders/import`: preview บอกว่าไฟล์นี้จะ "เปิดการ
 * แก้ไขใบเดิม" (`REVISION_RAISED`) แต่พอกดส่งจริง ใบเดิมถูก MRP วางแผนหรืองานส่งของ
 * ขยับไปแล้วโดยคนอื่นระหว่างนั้น — ไม่ใช่เรื่องสิทธิ์และไม่ใช่บั๊ก โหลดใบเดิมใหม่เพื่อ
 * ดูสถานะจริงก่อนตัดสินใจว่าจะทำอย่างไรต่อ
 */
export const SALES_ORDER_NOT_REVISABLE = 'SALES_ORDER_NOT_REVISABLE';

/* ------------------------------------- D-118 — ตัวกรองช่วงวันที่ และ Export --- */

/**
 * `422` — ช่วง "วันที่บันทึก" กลับหัว (จาก > ถึง)
 *
 * หน้าจอกันไว้ที่ฟอร์มด้วย **ไม่ใช่เพราะไม่เชื่อเซิร์ฟเวอร์** แต่เพราะช่วงกลับหัว
 * เป็นสิ่งที่หน้าจอรู้คำตอบอยู่แล้ว การยิงคำขอที่รู้ว่าจะโดน `422` คือการรอเดินทาง
 * ไปกลับหนึ่งรอบเพื่อฟังสิ่งที่รู้ก่อนกดแล้ว — และระหว่างนั้นตารางจะกระพริบเป็น
 * ความล้มเหลวทั้งที่ผู้ใช้แค่พิมพ์วันที่สองช่องไม่เรียงกัน
 */
export const SALES_ORDER_CREATED_RANGE_IS_REVERSED =
  'SALES_ORDER_CREATED_RANGE_IS_REVERSED';

/** `422` — ช่วง Delivery Date กลับหัว (กฎพี่น้องของข้างบน คนละช่วงกัน) */
export const SALES_ORDER_DELIVERY_RANGE_IS_REVERSED =
  'SALES_ORDER_DELIVERY_RANGE_IS_REVERSED';

/**
 * `422` — body ของ Export ไม่ได้บอกว่าจะเอาแถวไหนเลย (ไม่มีทั้ง `so_ids` และ `filters`)
 *
 * "ทุกใบที่ฉันมีสิทธิ์เห็น" คือ `filters: {}` ซึ่งเป็นประโยคที่ต้องมีคนเขียน ไม่ใช่
 * ค่าเริ่มต้นที่เกิดจากการลืมใส่ — หน้าจอนี้จึงส่ง `filters` เสมอเมื่อไม่มีการเลือก
 * รายใบ และกฎนี้เป็นด่านสำรองที่ผู้ใช้ไม่ควรต้องเจอจากหน้าจอนี้
 */
export const SALES_ORDER_EXPORT_NEEDS_A_SELECTION = 'SALES_ORDER_EXPORT_NEEDS_A_SELECTION';

/**
 * `422` — body ของ Export ส่งมาทั้ง `so_ids` และ `filters`
 *
 * สองอย่างนี้ขัดกันได้ และเซิร์ฟเวอร์จะต้องเดาว่าผู้ใช้หมายถึงอะไร — หน้าจอจึงเลือก
 * ทางใดทางหนึ่งตอนประกอบ body (`salesOrderExportRequest`) ไม่ใช่ส่งทั้งคู่ไปให้ตัดสิน
 */
export const SALES_ORDER_EXPORT_TAKES_IDS_OR_FILTERS =
  'SALES_ORDER_EXPORT_TAKES_IDS_OR_FILTERS';

/**
 * `422` — ตัวกรองปัจจุบันตรงมากกว่าเพดานที่ Export หนึ่งครั้งเขียนได้
 *
 * `details` พา `row_count` และ `max_rows` มาด้วย แต่หน้าจอ **แสดงข้อความของ
 * เซิร์ฟเวอร์** ไม่ประกอบประโยคใหม่จากสองเลขนั้น: เพดานเป็นของเซิร์ฟเวอร์ และสำเนา
 * ที่สองฝั่งเขียนเองจะไม่ตรงกันในวันที่เพดานเปลี่ยน
 */
export const SALES_ORDER_EXPORT_TOO_MANY_ROWS = 'SALES_ORDER_EXPORT_TOO_MANY_ROWS';

/* ------------------------------------------------------------- resource --- */

/**
 * หนึ่งบรรทัดของใบสั่งขาย — **บรรทัดของ Customer PO ที่เราคีย์เข้าระบบ**
 *
 * `customer_po_line_no` คือ **ตัวระบุบรรทัด** ของกลุ่มนี้ ไม่ใช่ `line_no` แบบใบ
 * สั่งซื้อ: contract บังคับให้มันไม่ซ้ำภายในใบเดียวกัน
 * (`422 CUSTOMER_PO_LINE_NO_DUPLICATED`) และมันคือเลขที่ลูกค้าใช้อ้างบรรทัดนั้น
 * กลับมาหาเรา การตั้งเลขบรรทัดของเราเองขึ้นมาซ้อนจะเป็นเลขที่ไม่มีใครในสองฝั่งใช้
 *
 * `quantity` เป็น **สตริง** ไม่ใช่ number — `NUMERIC(18, 4)` กว้างกว่าที่ IEEE-754
 * double เก็บได้ครบ การแปลงเป็น number ตรงนี้คือจุดที่หลักท้ายจะหายไปเงียบ ๆ และ
 * จำนวนที่ปัดแล้วคือจำนวนที่ลูกค้าไม่ได้สั่ง
 *
 * `requested_delivery_date` เป็น **instant ที่มี offset** (D-054) ไม่ใช่วันที่ลอย ๆ
 */
export interface SalesOrderLine {
  readonly customer_po_line_no: string;
  readonly product_id: string | null;
  readonly product_code: string;
  /** ทศนิยมที่เดินทางมาเป็นสตริงเสมอ — ห้ามแปลงเป็น number */
  readonly quantity: string;
  readonly uom_code: string;
  readonly requested_delivery_date: string;
  /**
   * D-107 ข้อ 2 — หกช่องอ้างอิงที่ไม่มีความหมายตายตัวต่อตำแหน่ง (`ref3` อาจเป็นเลข
   * สัญญาของลูกค้าคนหนึ่ง และเป็นเลข bin ของอีกคนก็ได้) `null` คือ "ไม่ได้บันทึก" ซึ่ง
   * ต้องแยกจาก `""` ("บันทึกเป็นค่าว่าง") ให้ขาดจากกันเสมอ — ดู
   * `apps/api/.../sales/spreadsheet.py`
   */
  readonly ref1: string | null;
  readonly ref2: string | null;
  readonly ref3: string | null;
  readonly ref4: string | null;
  readonly ref5: string | null;
  readonly ref6: string | null;
}

/**
 * หัวใบสั่งขายหนึ่งใบ **โดยไม่มีบรรทัด** — รูปของแต่ละ item ใน `GET /v1/sales-orders`
 *
 * ไม่มีคีย์ `lines` อยู่ในชนิดนี้เลย **โดยตั้งใจ** ไม่ใช่เพราะลืม: contract ตัด
 * บรรทัดออกจากรายการ (เหตุผลเดียวกับใบสั่งผลิต — routing ทั้งชุดต่อแถวเป็น payload
 * ใหญ่ที่ไม่มีใครใช้บนหน้า list) หน้าจอที่ต้องการบรรทัดจึงต้องเรียก
 * `fetchSalesOrder()` ของใบนั้น และ TypeScript เป็นผู้พิสูจน์ว่าไม่มีใครเผลออ่าน
 * บรรทัดจากแถวในรายการแล้วได้ `undefined` มาแสดงเป็น "ใบนี้ไม่มีสินค้า"
 *
 * ฟิลด์ที่เป็น `string | null` ทั้งหมดคือฟิลด์ที่ **contract ไม่ได้เอ่ยชื่อไว้ตรง ๆ**
 * (UUID คู่ของรหัส ผู้กระทำ และเวลา) — ดูเหตุผลที่ `parseSalesOrderSummary()`
 */
export interface SalesOrderSummary {
  /** UUID surrogate ที่เซิร์ฟเวอร์สร้าง (D-039) — ใช้เป็น path ของ transition */
  readonly so_id: string;
  /** business key ที่ผู้ใช้กรอกเอง unique ทั้งระบบ และแก้ไม่ได้หลังสร้าง */
  readonly so_number: string;
  readonly plant_id: string | null;
  readonly plant_code: string;
  readonly customer_id: string | null;
  readonly customer_code: string;
  /** เลขใบสั่งซื้อ **ของลูกค้า** — คู่ (ลูกค้า, เลขนี้) ต้องไม่ซ้ำทั้งระบบ */
  readonly customer_po_number: string;
  readonly status: string;
  readonly created_by_employee_id: string | null;
  readonly confirmed_by_employee_id: string | null;
  readonly confirmed_at: string | null;
  readonly cancelled_at: string | null;
  readonly note: string | null;
  readonly created_at: string | null;
  readonly updated_at: string | null;
}

/**
 * ใบสั่งขายหนึ่งใบ **พร้อมบรรทัดครบ** — รูปของ `GET /v1/sales-orders/{so_id}` และ
 * ของ `201` ที่ `POST` คืนมา
 */
export interface SalesOrder extends SalesOrderSummary {
  readonly lines: readonly SalesOrderLine[];
}

/**
 * หนึ่งแถวของ `GET /v1/sales-orders` — หัวใบ **บวกสี่ค่าที่รายการคำนวณให้** (D-118 ข้อ 4)
 *
 * ชนิดแยกจาก `SalesOrderSummary` **ไม่ใช่ฟิลด์ optional ที่แปะเพิ่ม** เพราะสี่ค่านี้
 * มาจากเส้นรายการเส้นเดียว: คำตอบของ `confirm`/`cancel` (`SalesOrderResponse`
 * ฝั่งเซิร์ฟเวอร์) ไม่มีมันเลย การรวมสองรูปเป็นชนิดเดียวที่ฟิลด์เป็น optional จะทำให้
 * ตารางอ่าน `created_date` ของแถวที่เพิ่งกดยืนยันได้เป็น `undefined` แล้วแสดงเป็น
 * ช่องว่างโดยไม่มีใครเห็นว่าผิด
 *
 * ทั้งสี่ **ไม่ใช่คอลัมน์ในฐานข้อมูล** — เซิร์ฟเวอร์คำนวณจากเอกสาร (`created_date`
 * คือวันตามปฏิทินไทยของ `created_at` ตาม D-055 ส่วนช่วงวันส่งและจำนวนบรรทัดมาจาก
 * บรรทัดของใบ) และทั้งสี่มีอยู่เพราะ **ตัวกรองที่กรองค่าที่ตารางไม่แสดง คือตัวกรอง
 * ที่ไม่มีใครตรวจได้ว่ามันทำงานถูก**
 */
export interface SalesOrderListItem extends SalesOrderSummary {
  /** วันตามปฏิทินไทย (D-055) ของ `created_at` — `YYYY-MM-DD` ไม่ใช่ instant ที่สอง */
  readonly created_date: string;
  /**
   * วันที่เร็วสุด/ช้าสุดที่บรรทัดของใบนี้ต้องการของ — **สองฟิลด์ ไม่ใช่ช่วงที่เรนเดอร์
   * มาแล้ว** หน้าจอเป็นผู้ตัดสินว่าช่วงอ่านอย่างไร (ดู `salesOrderDeliverySpanLabel`)
   * เท่ากันได้เมื่อทุกบรรทัดต้องการวันเดียวกัน และเป็น `null` ทั้งคู่ได้เฉพาะใบที่ไม่มี
   * บรรทัดเลย ซึ่งไม่มี route ใดสร้างได้
   */
  readonly delivery_date_from: string | null;
  readonly delivery_date_to: string | null;
  /** จำนวนบรรทัดของใบ — ค่าที่ตารางแสดงแทนบรรทัดที่ contract ตัดออก (D-090) */
  readonly line_count: number;
}

/**
 * `GET /v1/sales-orders` — envelope มาตรฐานของทุกกลุ่ม **ที่ item ไม่มีบรรทัด**
 *
 * `total` เข้ามาที่ D-118 ข้อ 1 และ **ไม่ได้ทำให้รายการนี้เป็น offset list**: ปุ่ม
 * "ก่อนหน้า" ยังเป็นกองของ cursor ที่เบราว์เซอร์เก็บไว้ ส่วน `total` มีไว้ให้คนอ่านออก
 * ว่ากำลังดูส่วนไหนของสมุดคำสั่งซื้ออยู่ ("แถว 26–50 จาก 413") ซึ่ง cursor บอกเองไม่ได้
 */
export interface SalesOrderListPage {
  readonly items: readonly SalesOrderListItem[];
  readonly next_cursor: string | null;
  /** จำนวนใบที่ **ตัวกรองชุดนี้** ตรงภายในขอบเขตโรงงานของผู้เรียก ไม่กว้างกว่านั้น */
  readonly total: number;
}

/* -------------------------------------------------------------- requests -- */

/**
 * หนึ่งบรรทัดที่ฟอร์มส่งออกไป — ห้าคีย์ตามที่ contract เขียนไว้บวก `ref1`..`ref6`
 * (D-107 ข้อ 2, D-116 ข้อ 8) และไม่มีคีย์ที่สิบสาม
 *
 * ต่างจากบรรทัดของใบสั่งซื้อซึ่งส่งได้แค่สองคีย์เพราะที่เหลือคัดลอกมาจากใบขอซื้อ:
 * ที่นี่ **ทั้งบรรทัดคือสิ่งที่ลูกค้าสั่ง** และไม่มีเอกสารภายในใบไหนที่เราจะคัดลอกมา
 *
 * `ref1`..`ref6` เป็น `string | null` เสมอ **ไม่ใช่ `string | undefined`** — ผู้เรียก
 * ต้องตัดสินใจตรงนี้แล้วว่าช่องว่างแปลว่า `null` (D-107 ข้อ 2: "ไม่ได้บันทึก" ต้อง
 * แยกจาก "บันทึกเป็นค่าว่าง" ให้ขาดจากกัน) ไม่ใช่ผลักภาระนั้นไปให้ตอน serialize
 */
export interface CreateSalesOrderLine {
  readonly customer_po_line_no: string;
  readonly product_code: string;
  /** สตริงเสมอ — `NUMERIC(18, 4)` กว้างกว่าที่ IEEE-754 double เก็บได้ครบ */
  readonly quantity: string;
  readonly uom_code: string;
  /** ISO-8601 ที่มี offset ติดมาด้วย (D-054) — ดู `toRequestedDeliveryDateIso()` */
  readonly requested_delivery_date: string;
  readonly ref1: string | null;
  readonly ref2: string | null;
  readonly ref3: string | null;
  readonly ref4: string | null;
  readonly ref5: string | null;
  readonly ref6: string | null;
}

/**
 * body ของ `POST /v1/sales-orders` **และของ `POST /v1/sales-orders/import`
 * (D-092)** — รูปเดียวกันเป๊ะ สองเส้นต่างกันแค่ตรงที่เส้นหลังตัดสินสามทางแทนที่จะ
 * ตอบ `409` เมื่อเจอ Customer PO ซ้ำ (ดู `client.ts`)
 *
 * **มีแต่ code ไม่มี UUID** ตามคอนเวนชันข้ามกลุ่มของ contract — ผู้ใช้กรอกรหัสที่
 * เขาอ่านออก เซิร์ฟเวอร์เป็นผู้แปลงเป็น id
 *
 * **ไม่มี `so_number`** (D-113, D-116 ข้อ 6) — เซิร์ฟเวอร์ออกเลขเองเป็น
 * `SO-<plant_code>-<YYMM>-<6 หลัก>` เสมอ ทั้งสองเส้นตอบ
 * `422 SO_NUMBER_IS_SERVER_ASSIGNED` ถ้าคีย์นี้ยังติดมาในคำขอ ฟิลด์ที่ contract
 * เรียกว่าเป็นของเซิร์ฟเวอร์อื่น ๆ (`so_id`, `status`, ผู้เปิด/ผู้ยืนยัน และ
 * timestamp ทั้งหมด) ก็ไม่ปรากฏที่นี่เลยแม้แต่คีย์เดียวเช่นกัน — ไม่ใช่แค่ "ไม่ส่ง"
 * แต่ **ไม่มีที่ให้ใส่** เหมือน `CreatePurchaseOrderBody`
 */
export interface CreateSalesOrderBody {
  readonly plant_code: string;
  readonly customer_code: string;
  readonly customer_po_number: string;
  readonly note?: string;
  /** อย่างน้อยหนึ่งบรรทัด — ว่างเปล่า → `422 SALES_ORDER_HAS_NO_LINES` */
  readonly lines: readonly CreateSalesOrderLine[];
}
